import { Paper, Stack } from "@mui/material";
import { SKIP_BUTTON_SECONDS } from "../constants";
import { useVideoControl } from "../contexts/VideoControlContext";
import { useSkipAndStepHandlers } from "../hooks/useSkipAndStepHandlers";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import { Dayjs } from "dayjs";
import { ReactNode } from "react";
import { VideoTimeline } from "./VideoTimeline";
import { PlaybackControls } from "./PlaybackControls";
import { AreaMaterialSnapshot } from "components/scrapEyeLabelling/types";
import { ShortLabelDetail } from "./VideoTimelineShortLabelTrack";

type VideoControlsProps = Readonly<{
  events?: ScrapEyesEvent[];
  selectedEventId?: string | null;
  onEventClick?: (eventId: string) => void;
  videoStart?: Dayjs;
  colorEventsBy?: "type" | "material";
  materialNameSource?: "labelling" | "raw";
  areaMaterialSnapshots?: AreaMaterialSnapshot[];
  onMappingSnapshotClick?: (snapshot: AreaMaterialSnapshot) => void;
  selectedSnapshotTimestamp?: number | null;
  showShortLabels?: boolean;
  shortLabelDetail?: ShortLabelDetail;
  highlightedLane?: string;
  actionButtons?: ReactNode;
  centerActionButtons?: ReactNode;
  location?: string;
}>;

export const VideoControls = ({
  location,
  events,
  selectedEventId,
  onEventClick,
  videoStart,
  colorEventsBy,
  materialNameSource,
  areaMaterialSnapshots,
  onMappingSnapshotClick,
  selectedSnapshotTimestamp,
  showShortLabels,
  shortLabelDetail,
  highlightedLane,
  actionButtons,
  centerActionButtons,
}: VideoControlsProps) => {
  const { state, actions } = useVideoControl();

  const {
    handleSkipBackward,
    handleSkipForward,
    handleStepBackward,
    handleStepForward,
  } = useSkipAndStepHandlers({ skipSeconds: SKIP_BUTTON_SECONDS });

  return (
    <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
      <Stack spacing={2}>
        <VideoTimeline
          location={location}
          currentTime={state.currentTime}
          duration={state.duration}
          events={events}
          selectedEventId={selectedEventId}
          onSeek={actions.seekTo}
          onEventClick={onEventClick}
          videoStart={videoStart}
          colorEventsBy={colorEventsBy}
          materialNameSource={materialNameSource}
          areaMaterialSnapshots={areaMaterialSnapshots}
          onMappingSnapshotClick={onMappingSnapshotClick}
          selectedSnapshotTimestamp={selectedSnapshotTimestamp}
          showShortLabels={showShortLabels}
          shortLabelDetail={shortLabelDetail}
          highlightedLane={highlightedLane}
        />

        <PlaybackControls
          isPlaying={state.isPlaying}
          playbackRate={state.playbackRate}
          onTogglePlayPause={actions.togglePlayPause}
          onSkipBackward={handleSkipBackward}
          onSkipForward={handleSkipForward}
          onSpeedChange={actions.setPlaybackRate}
          videoStart={videoStart}
          actionButtons={actionButtons}
          centerActionButtons={centerActionButtons}
          onStepBackward={handleStepBackward}
          onStepForward={handleStepForward}
        />
      </Stack>
    </Paper>
  );
};
